/**
 * The "New product" dialog, shared by the Products page and the sidebar's
 * product switcher (Stage A). Self-contained: name + optional description,
 * the create mutation, inline error. `onCreated` fires with the fresh
 * detail; both callers use it to land on the new product's home with the
 * connect-a-codebase wizard already open (2026-09-05; owner: "Should we just
 * have 2nd screen???"), because the thing you just made is the thing you
 * want to be looking at, and the one thing to do with it is connect code.
 */

import { useState } from 'react';

import { useCreateProduct } from '../../api/queries';
import type { ProductDetail } from '../../api/types';
import { Button, Dialog, ErrorNote, FieldLabel, TextInput } from '../../lib/ui';

export function CreateProductDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (detail: ProductDetail) => void;
}) {
  const createProduct = useCreateProduct();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const close = () => {
    setName('');
    setDescription('');
    createProduct.reset();
    onClose();
  };

  const submit = () => {
    if (name.trim().length === 0 || createProduct.isPending) return;
    createProduct.mutate(
      {
        name: name.trim(),
        ...(description.trim().length > 0 ? { description: description.trim() } : {}),
      },
      {
        onSuccess: (detail) => {
          close();
          onCreated?.(detail);
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={close} onSubmit={submit} title="New product">
      <div className="space-y-4" data-testid="create-product">
        <div>
          <FieldLabel>Name</FieldLabel>
          {/* The dialog puts focus here on open (lib/ui Dialog): the first field. */}
          <TextInput value={name} onChange={setName} ariaLabel="Product name" />
        </div>
        <div>
          <FieldLabel>Description (optional)</FieldLabel>
          <TextInput value={description} onChange={setDescription} ariaLabel="Product description" />
        </div>
        <p className="text-tertiary">
          One product is one piece of software: the code loremap reads, the map it draws from
          it, and every conversation about it. Next, connect its code.
        </p>
        <ErrorNote>{createProduct.isError ? createProduct.error.message : null}</ErrorNote>
        <div className="flex justify-end gap-2">
          <Button kind="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            kind="primary"
            disabled={name.trim().length === 0 || createProduct.isPending}
            onClick={submit}
          >
            {createProduct.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
